import { type ReactNode } from 'react';
import {
  ResponsiveContainer, BarChart as RBarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend
} from 'recharts';
import { useChartTheme, resolveSeriesColors } from './theme.js';
import { ChartTooltip } from './ChartTooltip.js';
import type { BaseChartProps } from './_internal/shared-types.js';

export interface BarChartProps extends BaseChartProps {
  /** Stack the series. */
  stacked?: boolean;
  /** Horizontal layout (bars run left→right). */
  horizontal?: boolean;
  /** Bar corner radius in px. Default 4. */
  barRadius?: number;
  /** Optional max-width per bar. */
  maxBarSize?: number;
}

/** Themed bar chart with optional stacking + horizontal layout. */
export function BarChart(props: BarChartProps): ReactNode {
  const {
    data, dataKey, series,
    h = 280, w = '100%',
    withTooltip = true, withLegend = true,
    withXAxis = true, withYAxis = true, withGrid = true,
    valueFormatter, labelFormatter, tooltipContent,
    stacked, horizontal, barRadius = 4, maxBarSize,
    className
  } = props;
  const theme = useChartTheme();
  const resolved = resolveSeriesColors(series, theme);

  return (
    <div className={['rr-chart', 'rr-chart--bar', className].filter(Boolean).join(' ')} style={{ width: w, height: h }}>
      <ResponsiveContainer width="100%" height="100%">
        <RBarChart data={data} layout={horizontal ? 'vertical' : 'horizontal'}>
          {withGrid ? <CartesianGrid stroke={theme.grid} strokeDasharray="3 3" /> : null}
          {horizontal ? (
            <>
              {withXAxis ? <XAxis type="number" stroke={theme.axis} fontSize={11} tickFormatter={valueFormatter as never} tickLine={false} axisLine={{ stroke: theme.grid }} /> : null}
              {withYAxis ? <YAxis type="category" dataKey={dataKey} stroke={theme.axis} fontSize={11} tickLine={false} axisLine={{ stroke: theme.grid }} /> : null}
            </>
          ) : (
            <>
              {withXAxis ? <XAxis dataKey={dataKey} stroke={theme.axis} fontSize={11} tickFormatter={labelFormatter as never} tickLine={false} axisLine={{ stroke: theme.grid }} /> : null}
              {withYAxis ? <YAxis stroke={theme.axis} fontSize={11} tickFormatter={valueFormatter as never} tickLine={false} axisLine={{ stroke: theme.grid }} /> : null}
            </>
          )}
          {withTooltip ? <Tooltip cursor={{ fill: theme.grid, opacity: 0.4 }} content={(tooltipContent ?? <ChartTooltip formatValue={valueFormatter} formatLabel={labelFormatter} />) as never} /> : null}
          {withLegend ? <Legend wrapperStyle={{ fontFamily: 'var(--rr-font-system)', fontSize: 12, color: theme.axisLabel }} /> : null}
          {resolved.map((s) => (
            <Bar
              key={s.name}
              dataKey={s.name}
              name={s.label ?? s.name}
              fill={s.color}
              stackId={stacked ? 'stack' : undefined}
              radius={[barRadius, barRadius, 0, 0]}
              maxBarSize={maxBarSize}
              isAnimationActive
            />
          ))}
        </RBarChart>
      </ResponsiveContainer>
    </div>
  );
}
